import React, { useEffect, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import BlockIcon from '@mui/icons-material/Block';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import Tabular from '../../../components/Tabular/Tabular';
import strings from '../../../common/Translation/Translate';
import { Divider, TableCell, TableRow } from '@mui/material';

interface BookingTableInfoProps {
  item: any;
  focusSection?: 'unavailable' | 'highDemand';
}

const columnAlign: Array<'left' | 'right'> = ['left', 'right'];
const columnWidths = ['58%', '42%'];
const tableSx = { tableLayout: 'fixed', width: '100%' };
const firstColumnSx = { wordBreak: 'break-word' };
const secondColumnSx = { wordBreak: 'break-word' };

export default function BookingTableInfo({ item, focusSection }: BookingTableInfoProps) {
  const { unavailableDates = [], highDemandDates = [] } = item ?? {};
  const unavailableRef = useRef<HTMLDivElement>(null);
  const highDemandRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusSection === 'unavailable') {
      unavailableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (focusSection === 'highDemand') {
      highDemandRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [focusSection, unavailableDates, highDemandDates]);

  if (unavailableDates.length === 0 && highDemandDates.length === 0) {
    return (
      <Typography sx={{ textAlign: 'center', py: 3 }}>{strings.no_data_found}</Typography>
    );
  }

  const showBoth = unavailableDates.length > 0 && highDemandDates.length > 0;

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      {unavailableDates.length > 0 && (
        <Box ref={unavailableRef}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <BlockIcon sx={{ fontSize: 20, color: '#98A2B3' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              {strings.unavailable_header}
            </Typography>
          </Box>
          <Tabular
            rows={[strings.unavailable_venue, strings.unavailable_segment]}
            columnAlign={columnAlign}
            columnWidths={columnWidths}
            sx={tableSx}>
            {unavailableDates.map((row: any) => (
              <TableRow key={`${row.venue}-${row.eventType}`}>
                <TableCell scope="row" sx={firstColumnSx}>{row.venue}</TableCell>
                <TableCell scope="row" align="right" sx={secondColumnSx}>{row.eventType}</TableCell>
              </TableRow>
            ))}
          </Tabular>
        </Box>
      )}

      {showBoth && <Divider sx={{ my: 3 }} />}

      {highDemandDates.length > 0 && (
        <Box ref={highDemandRef}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LocalFireDepartmentIcon sx={{ fontSize: 20, color: '#f85f18' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              {strings.high_demand_header}
            </Typography>
          </Box>
          <Tabular
            rows={[strings.venueConfig_general_highDemandName, strings.venueConfig_general_highDemandVenue]}
            columnAlign={columnAlign}
            columnWidths={columnWidths}
            sx={tableSx}>
            {highDemandDates.map((row: any) => (
              <TableRow key={`${row.venue}-${row.highDemandCategory}`}>
                <TableCell sx={firstColumnSx}>{row.venue}</TableCell>
                <TableCell align="right" sx={secondColumnSx}>{row.highDemandCategory}</TableCell>
              </TableRow>
            ))}
          </Tabular>
        </Box>
      )}
    </Box>
  );
}
